// React Query hooks over the data provider. Thin by design: each hook is a query key,
// a provider call and (for mutations) the invalidations the screens rely on.
//
// Query hooks take (params?, { query?: <react-query options> }); mutation hooks take
// `{ id?, data? }` variables — the same call shapes the vendor screens already use.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { dataProvider as p } from "@/lib/data-provider";
import type {
  AssignedPath,
  ListParams,
  Notification,
  PathSession,
  PathsParams,
  TrackSelection,
  User,
  UserPreferences,
  UserSignup,
} from "./types";

export const queryKeys = {
  me: ["users", "me"] as const,
  terms: ["users", "terms"] as const,
  dashboard: ["users", "dashboard"] as const,
  preferences: ["users", "preferences"] as const,
  syncHealthData: ["users", "sync-health-data"] as const,
  paths: (params?: PathsParams) => ["paths", params ?? {}] as const,
  path: (id: number) => ["paths", "detail", id] as const,
  bookmarkedSessions: (pathId: number) => ["bookmark", pathId] as const,
  allBookmarkedSessions: (params?: ListParams) => ["bookmark", "all", params ?? {}] as const,
  notifications: (params?: ListParams) => ["notifications", params ?? {}] as const,
  questionSuggestions: (params?: { search?: string }) => ["question-suggestions", params ?? {}] as const,
  recentSearches: ["recent-searches"] as const,
  healthSummary: (kind: string, params?: ListParams) => [kind, params ?? {}] as const,
};

type QueryOpts<T> = { query?: Partial<UseQueryOptions<T, Error, T, QueryKey>> };
type MutationOpts<TData, TVars> = { mutation?: Partial<UseMutationOptions<TData, Error, TVars>> };

function q<T>(key: QueryKey, fn: () => Promise<T>, options?: QueryOpts<T>) {
  return useQuery<T, Error, T, QueryKey>({ queryKey: key, queryFn: fn, ...(options?.query ?? {}) });
}

// ---------------------------------------------------------------------------
// Me
// ---------------------------------------------------------------------------
export const useMe = (options?: QueryOpts<User>) => q(queryKeys.me, () => p.me(), options);
export const useTermsAccepted = (options?: QueryOpts<Awaited<ReturnType<typeof p.termsAccepted>>>) =>
  q(queryKeys.terms, () => p.termsAccepted(), options);
export const useDashboard = (options?: QueryOpts<Awaited<ReturnType<typeof p.dashboard>>>) =>
  q(queryKeys.dashboard, () => p.dashboard(), options);
export const usePreferences = (options?: QueryOpts<UserPreferences>) =>
  q(queryKeys.preferences, () => p.preferences(), options);
export const useSyncHealthData = (options?: QueryOpts<Awaited<ReturnType<typeof p.syncHealthData>>>) =>
  q(queryKeys.syncHealthData, () => p.syncHealthData(), options);

export function useUpdateMe(options?: MutationOpts<User, { id?: number; data: Partial<User> }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { id?: number; data: Partial<User> }) => p.updateMe(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
    ...(options?.mutation ?? {}),
  });
}
export function useUpdateProfile(options?: MutationOpts<User, { data: Partial<User> }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: Partial<User> }) => p.updateProfile(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
    ...(options?.mutation ?? {}),
  });
}
export function useUpdatePreferences(options?: MutationOpts<UserPreferences, { data: Partial<UserPreferences> }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: Partial<UserPreferences> }) => p.updatePreferences(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.preferences }),
    ...(options?.mutation ?? {}),
  });
}
export function useCheckHealthData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (_vars?: unknown) => p.checkHealthData(),
    onSuccess: () => qc.invalidateQueries(),
  });
}
export const useDeactivate = () => useMutation({ mutationFn: (_vars?: unknown) => p.deactivate() });

// ---------------------------------------------------------------------------
// Paths / sessions
// ---------------------------------------------------------------------------
export const usePaths = (params?: PathsParams, options?: QueryOpts<Awaited<ReturnType<typeof p.paths>>>) =>
  q(queryKeys.paths(params), () => p.paths(params), options);
export const usePath = (id: number, options?: QueryOpts<AssignedPath>) =>
  q(queryKeys.path(id), () => p.path(id), { query: { enabled: !!id, ...(options?.query ?? {}) } });
export const useBookmarkedSessions = (pathId: number, options?: QueryOpts<PathSession[]>) =>
  q(queryKeys.bookmarkedSessions(pathId), () => p.bookmarkedSessions(pathId), {
    query: { enabled: !!pathId, ...(options?.query ?? {}) },
  });
export const useAllBookmarkedSessions = (params?: ListParams, options?: QueryOpts<Awaited<ReturnType<typeof p.allBookmarkedSessions>>>) =>
  q(queryKeys.allBookmarkedSessions(params), () => p.allBookmarkedSessions(params), options);

const invalidatePaths = (qc: ReturnType<typeof useQueryClient>) =>
  Promise.all([
    qc.invalidateQueries({ queryKey: ["paths"] }),
    qc.invalidateQueries({ queryKey: ["bookmark"] }),
    qc.invalidateQueries({ queryKey: queryKeys.dashboard }),
    qc.invalidateQueries({ queryKey: ["notifications"] }),
  ]);

export function useStartPath() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ id }: { id: number }) => p.startPath(id), onSuccess: () => invalidatePaths(qc) });
}
export function useEndPath() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ id }: { id: number }) => p.endPath(id), onSuccess: () => invalidatePaths(qc) });
}
export function useUpdateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<PathSession> }) => p.updateSession(id, data),
    onSuccess: () => invalidatePaths(qc),
  });
}
/** Text-to-speech is hidden (hidden-features.listen); kept so the screens compile until TTS exists. */
export const usePathSpeech = (id: number, options?: QueryOpts<Blob>) =>
  q(["paths", "speech", id], () => Promise.reject(new Error("Text-to-speech is not available")), {
    query: { enabled: false, ...(options?.query ?? {}) },
  });

// ---------------------------------------------------------------------------
// Notifications — polled; the WebSocket feed went with the old backend.
// ---------------------------------------------------------------------------
export const NOTIFICATIONS_POLL_MS = 60_000;

export const useNotifications = (params?: ListParams, options?: QueryOpts<Awaited<ReturnType<typeof p.notifications>>>) =>
  q(queryKeys.notifications(params), () => p.notifications(params), {
    query: { refetchInterval: NOTIFICATIONS_POLL_MS, ...(options?.query ?? {}) },
  });
export function useUpdateNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Notification> }) => p.updateNotification(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}
export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) => p.deleteNotification(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

// ---------------------------------------------------------------------------
// PathFinder
// ---------------------------------------------------------------------------
export const useQuestionSuggestions = (params?: { search?: string }, options?: QueryOpts<Awaited<ReturnType<typeof p.questionSuggestions>>>) =>
  q(queryKeys.questionSuggestions(params), () => p.questionSuggestions(params), options);
export const useRecentSearches = (options?: QueryOpts<Awaited<ReturnType<typeof p.recentSearches>>>) =>
  q(queryKeys.recentSearches, () => p.recentSearches(), options);
export function useDeleteRecentSearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ search_id }: { search_id: number | string }) => p.deleteRecentSearch(search_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.recentSearches }),
  });
}
export function useClearRecentSearches() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (_vars?: unknown) => p.clearRecentSearches(),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.recentSearches }),
  });
}
export function useTrackSelection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: TrackSelection }) => p.trackSelection(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.recentSearches }),
  });
}

// ---------------------------------------------------------------------------
// Health summary
// ---------------------------------------------------------------------------
const list =
  <K extends "conditions" | "encounters" | "observations" | "medicationRequests" | "medicationAdministrations" | "procedures" | "serviceRequests" | "practitioners">(kind: K) =>
  (params?: ListParams, options?: QueryOpts<Awaited<ReturnType<(typeof p)[K]>>>) =>
    q(queryKeys.healthSummary(kind, params), () => p[kind](params) as Promise<Awaited<ReturnType<(typeof p)[K]>>>, options);

export const useConditions = list("conditions");
export const useEncounters = list("encounters");
export const useObservations = list("observations");
export const useMedicationRequests = list("medicationRequests");
export const useMedicationAdministrations = list("medicationAdministrations");
export const useProcedures = list("procedures");
export const useServiceRequests = list("serviceRequests");
export const usePractitioners = list("practitioners");

// ---------------------------------------------------------------------------
// Sign-up: Firebase owns the credentials; this registers the profile fields against
// the signed-in Firebase user.
// ---------------------------------------------------------------------------
export function useSignUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: UserSignup }) => p.signUp(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}
